import {
	ApplicationFinancingTypeEnum,
	ApplicationStateEnum,
	OrderStateEnum,
} from '.spec/self-care/tsTypes';
import { TransactionCardVariant } from '@mallpay/orbis';
import cx from 'classnames';
import { filter, map } from 'lodash/fp';
import React from 'react';

import { Amount } from '@skippay/core/@types/amount';
import { Application, Installment } from '@skippay/core/@types/payInThree';
import {
	TransactionBoxItem,
	TransactionBoxItemState,
} from '@skippay/core/@types/transactionBoxItem';
import iconDefault from '@skippay/core/assets/icons/transaction/icon-default.svg';
import iconPayInThree from '@skippay/core/assets/icons/transaction/icon-pay-in-three.svg';
import { ReactComponent as IconCrossCircle } from '@skippay/core/assets/icons/transaction/icon-remove.svg';
import { ReactComponent as IconReturned } from '@skippay/core/assets/icons/transaction/icon-returned.svg';
import { ReactComponent as IconWarning } from '@skippay/core/assets/icons/transaction/icon-warning.svg';
import FormattedCurrency from '@skippay/core/components/FormattedCurrency';
import FormattedDate from '@skippay/core/components/FormattedDate';
import Text from '@skippay/core/components/Text';
import { FormattedMessage, IntlShape, defineMessages } from '@skippay/core/i18n/intl';
import { sumAmounts, toFloat } from '@skippay/core/utils/amountUtils';
import { QueryFilter, parseMinorUnits } from '@skippay/core/utils/apiUtils';

import { PAY_IN_THREE_DETAIL } from 'constants/routes';
import { assertUnreachable } from 'utils/types';

export const CANCELLED_OR_PROCESSING = 'cancelledOrProcessing';
export const ONCOMING = 'oncoming';
export const OVERDUE = 'overdue';
export const PAID = 'paid';
export const PROCESSING = 'processing';

export type InstallmentState =
	| typeof CANCELLED_OR_PROCESSING
	| typeof ONCOMING
	| typeof OVERDUE
	| typeof PAID;

export const getRemainingInstallments = (installments: Installment[]): Installment[] => {
	return filter((installment) => installment.balance.amount > 0, installments);
};

export const getRemainingAmountToPay = (application: Application): Amount => {
	if (application.state === ApplicationStateEnum.PROCESSING) {
		return application.order.totalPrice;
	}
	const amounts = map(
		'balance',
		getRemainingInstallments(application.financing?.payInThree?.installments || [])
	);
	return sumAmounts(amounts);
};

export const getInstallmentState = (installment: Installment | undefined): InstallmentState => {
	if (installment?.claimWithoutSanctions.amount === 0) {
		return CANCELLED_OR_PROCESSING;
	}
	if (installment?.paymentDate) {
		return PAID;
	}
	if (installment?.currentDpd && installment?.currentDpd > 0) {
		return OVERDUE;
	}
	return ONCOMING;
};

export const hasOverdueInstallments = (application: Application): boolean => {
	return (
		(application.state === ApplicationStateEnum.READY &&
			application.financing?.payInThree?.installments.some(
				(installment) =>
					!installment.paymentDate && installment.currentDpd && installment.currentDpd > 0
			)) ||
		false
	);
};

export const nearestUnpaidInstallment = (
	installments: readonly Installment[] = []
): Installment | undefined =>
	installments
		.filter((installment) => !installment.paymentDate)
		.sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)))[0];

export const getPayInThreeQueryFilter = (): QueryFilter =>
	new QueryFilter().prop('type').eq(ApplicationFinancingTypeEnum.PAY_IN_THREE);

type StatusCase =
	| { type: 'cancelled' }
	| { type: 'nextPayment'; dueDate: string | undefined }
	| { type: 'overdue'; dueDate: string }
	| { type: 'paid'; paymentDate: string }
	| { type: 'processing' }
	| { type: 'rejected' }
	| { type: 'returned' };

const STATUS_MESSAGES: Record<StatusCase['type'], { defaultMessage: string; id: string }> =
	defineMessages({
		cancelled: {
			id: 'payInThreeApplication.list.cancelled.title',
			defaultMessage: 'Zrušeno',
		},
		nextPayment: {
			id: 'payInThreeApplication.list.nextPayment.title',
			defaultMessage: 'Příští platba: {date}',
		},
		overdue: {
			id: 'payInThreeApplication.list.nextPayment.overdue.title',
			defaultMessage: 'Po splatnosti: {date}',
		},
		paid: {
			id: 'payInThreeApplication.list.paid.title',
			defaultMessage: 'Doplaceno dne {paymentDate}',
		},
		processing: {
			id: 'payInThreeApplication.list.processing.title',
			defaultMessage: 'Nedokončená objednávka',
		},
		rejected: {
			id: 'payInThreeApplication.list.rejected.title',
			defaultMessage: 'Zamítnuto',
		},
		returned: {
			id: 'payInThreeApplication.list.returned.title',
			defaultMessage: 'Vráceno',
		},
	});

// Shared by getState (JSX, used by Overview/Receipts/Active) and getHistoryStatusText (plain string,
// used by the Orbis TransactionCard) so the state-decision branches live in exactly one place.
const getStatusCase = (
	application: Application,
	nextPayment: Installment | undefined
): StatusCase => {
	if (application.state === ApplicationStateEnum.PROCESSING) {
		return { type: 'processing' };
	}
	if (application.state === ApplicationStateEnum.REJECTED) {
		return { type: 'rejected' };
	}
	if (application.order.state === OrderStateEnum.RETURNED) {
		return { type: 'returned' };
	}
	if (application.state === ApplicationStateEnum.CANCELLED) {
		return { type: 'cancelled' };
	}
	if (application.state === ApplicationStateEnum.READY && application.isClosed) {
		return { type: 'paid', paymentDate: application.financing.paymentDate };
	}
	if (
		application.state === ApplicationStateEnum.READY &&
		nextPayment?.currentDpd &&
		nextPayment.currentDpd > 0
	) {
		return { type: 'overdue', dueDate: nextPayment.dueDate };
	}
	return { type: 'nextPayment', dueDate: nextPayment?.dueDate };
};

const getState = (
	application: Application,
	nextPayment: Installment | undefined
): TransactionBoxItemState => {
	const statusCase = getStatusCase(application, nextPayment);
	const descriptor = STATUS_MESSAGES[statusCase.type];

	switch (statusCase.type) {
		case 'processing':
			return {
				color: 'colorWarning',
				text: <FormattedMessage {...descriptor} />,
			};
		case 'rejected':
			return {
				color: 'colorError',
				icon: <IconWarning />,
				text: <FormattedMessage {...descriptor} />,
			};
		case 'returned':
			return {
				color: 'colorSuccess',
				icon: <IconReturned />,
				text: <FormattedMessage {...descriptor} />,
			};
		case 'cancelled':
			return {
				color: 'colorWarning',
				icon: <IconCrossCircle />,
				text: <FormattedMessage {...descriptor} />,
			};
		case 'paid':
			return {
				color: 'colorSuccess',
				text: (
					<FormattedMessage
						{...descriptor}
						values={{ paymentDate: <FormattedDate value={statusCase.paymentDate} /> }}
					/>
				),
			};
		case 'overdue':
			return {
				color: 'colorError',
				text: (
					<FormattedMessage
						{...descriptor}
						values={{ date: <FormattedDate value={statusCase.dueDate} /> }}
					/>
				),
			};
		case 'nextPayment':
			return {
				color: 'colorExtraLight',
				text: (
					<FormattedMessage
						{...descriptor}
						values={{ date: <FormattedDate value={statusCase.dueDate} /> }}
					/>
				),
			};
		default:
			return assertUnreachable(statusCase);
	}
};

const HISTORY_STATUS_VARIANT_BY_COLOR: Record<
	TransactionBoxItemState['color'],
	TransactionCardVariant
> = {
	colorError: 'error',
	colorSuccess: 'success',
	colorWarning: 'warning',
	colorExtraLight: 'default',
	colorBase: 'default',
};

// TileRow-based TransactionCard from Orbis only accepts a plain string, unlike the JSX `stateProps.text`
// used by Overview/Receipts/Active which stays untouched to avoid widening a type shared across those screens.
export const getHistoryStatusText = (application: Application, intl: IntlShape): string => {
	const nextPayment = application.financing.payInThree.installments.find(
		(installment) => !installment.paymentDate
	);
	const statusCase = getStatusCase(application, nextPayment);

	switch (statusCase.type) {
		case 'paid':
			return intl.formatMessage(STATUS_MESSAGES.paid, {
				paymentDate: intl.formatDate(statusCase.paymentDate),
			});
		case 'overdue':
			return intl.formatMessage(STATUS_MESSAGES.overdue, {
				date: intl.formatDate(statusCase.dueDate),
			});
		case 'nextPayment':
			return intl.formatMessage(STATUS_MESSAGES.nextPayment, {
				date: statusCase.dueDate ? intl.formatDate(statusCase.dueDate) : '',
			});
		case 'cancelled':
			return intl.formatMessage(STATUS_MESSAGES.cancelled);
		case 'processing':
			return intl.formatMessage(STATUS_MESSAGES.processing);
		case 'rejected':
			return intl.formatMessage(STATUS_MESSAGES.rejected);
		case 'returned':
			return intl.formatMessage(STATUS_MESSAGES.returned);
		default:
			return assertUnreachable(statusCase);
	}
};

export const getHistoryStatusVariant = (
	color: TransactionBoxItemState['color'] | undefined
): TransactionCardVariant => (color ? HISTORY_STATUS_VARIANT_BY_COLOR[color] : 'default');

export const getHistoryAmountText = (application: Application, intl: IntlShape): string =>
	intl.formatNumber(parseMinorUnits(application.order.totalPrice.amount), {
		style: 'currency',
		currency: application.order.totalPrice.currency,
	});

export const payInThreeToTransactionBoxItem = (
	data: {
		application: Application;
	},
	receiptItem?: boolean
): TransactionBoxItem => {
	const { application } = data;

	const receiptName = application.additionalInfoReceipt?.name;
	const nextPayment = application.financing.payInThree.installments.find(
		(installment) => !installment.paymentDate
	);
	const canApplyForInstallments = application.canApplyForInstallments ?? false;
	const { additionalInfoInstallments } = application;

	return {
		date: application.createdDate,
		id: application.id,
		price: (
			<Text as="strong" className="print-colorBase">
				<FormattedCurrency {...application.order.totalPrice} />
			</Text>
		),
		name: {
			title: receiptItem
				? `${receiptName} - ${application.eshopAdditionalInfo.name}`
				: application.eshopAdditionalInfo.name,
		},
		logo: receiptItem ? iconPayInThree : application.eshopAdditionalInfo.logoUrl || iconDefault,
		stateProps: receiptItem ? undefined : getState(application, nextPayment),
		to: {
			pathname: PAY_IN_THREE_DETAIL,
			params: { applicationId: application.id },
		},
		hasReceipt: !!receiptName,
		isPayInThree: true,
		isApplication: true,
		isApplicationInProcess: application.state === ApplicationStateEnum.PROCESSING,
		nextPaymentValue:
			nextPayment && toFloat(nextPayment.balance) > 0 ? (
				<Text
					as="strong"
					className={cx(
						'text-right text-xs',
						nextPayment.currentDpd && nextPayment.currentDpd > 0
							? 'text-red-500'
							: 'text-text-additional'
					)}
					size="paragraphSmall"
				>
					<FormattedCurrency {...nextPayment.balance} />
				</Text>
			) : undefined,
		canApplyForInstallments,
		additionalInfoInstallments,
	};
};

export const payInThreeToTransactionToReceipts = (
	applications: Application[]
): TransactionBoxItem[] => {
	return applications
		?.map((application) => payInThreeToTransactionBoxItem({ application }, true))
		.filter(Boolean);
};
